import React, { useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Paper, Stack, TextField, Typography,
} from '@mui/material';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { db } from '../../firebase/firebase';
import { closeTenant, purgeClosedTenantNow, restoreTenant } from '../../sitePublishing/api';

type TenantClosureState = {
    slug: string | null;
    lifecycleState: string;
    purgeAfter: Date | null;
    closedAt: Date | null;
    stripe: string | null;
};

const asDate = (value: unknown): Date | null => value instanceof Timestamp ? value.toDate() : null;

const loadClosureState = async (tenantId: string): Promise<TenantClosureState> => {
    const snapshot = await getDoc(doc(db, 'booking-websites', tenantId));
    const data = snapshot.data() ?? {};
    const closure = (data.closure ?? {}) as Record<string, unknown>;
    return {
        slug: typeof data.slug === 'string' && data.slug.trim() ? data.slug.trim() : null,
        lifecycleState: typeof data.lifecycleState === 'string' ? data.lifecycleState : 'unknown',
        purgeAfter: asDate(closure.purgeAfter),
        closedAt: asDate(closure.closedAt),
        stripe: typeof closure.stripe === 'string' ? closure.stripe : null,
    };
};

const formatDate = (date: Date | null): string =>
    date ? date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'an unknown date';

const stripeOutcome: Record<string, string> = {
    canceled: 'Its Stripe subscription was cancelled.',
    none: 'It had no Stripe subscription.',
    skipped: 'Stripe was not contacted in this environment.',
    failed: 'Cancelling its Stripe subscription failed. Cancel it in the Stripe dashboard.',
};

type ConfirmDialogProps = {
    open: boolean;
    title: string;
    confirmation: string;
    confirmLabel: string;
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onConfirm: () => void;
    children: React.ReactNode;
};

const ConfirmByTypingDialog: React.FC<ConfirmDialogProps> = ({
    open, title, confirmation, confirmLabel, pending, error, onClose, onConfirm, children,
}) => {
    const [typed, setTyped] = useState('');
    const close = () => {
        setTyped('');
        onClose();
    };
    return (
        <Dialog open={open} onClose={pending ? undefined : close} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {children}
                    <Typography>
                        Type <strong>{confirmation}</strong> to confirm.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        value={typed}
                        onChange={(event) => setTyped(event.target.value)}
                        disabled={pending}
                        inputProps={{ 'aria-label': 'Confirmation', autoComplete: 'off' }}
                    />
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={pending}>Cancel</Button>
                <Button color="error" variant="contained" disabled={pending || typed.trim() !== confirmation}
                    onClick={onConfirm}>
                    {pending ? 'Working…' : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TenantClosureSection: React.FC<{ tenantId: string; businessName?: string }> = ({ tenantId, businessName }) => {
    const queryClient = useQueryClient();
    const [dialog, setDialog] = useState<'close' | 'purge' | null>(null);
    const stateQuery = useQuery({
        queryKey: ['tenant-closure', tenantId],
        queryFn: () => loadClosureState(tenantId),
    });
    const refresh = () => Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tenant-closure', tenantId] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-publish-status', tenantId] }),
    ]);
    const confirmation = stateQuery.data?.slug ?? tenantId;
    const closeMutation = useMutation({
        mutationFn: () => closeTenant(tenantId, confirmation),
        onSuccess: async () => {
            setDialog(null);
            await refresh();
        },
    });
    const restoreMutation = useMutation({ mutationFn: () => restoreTenant(tenantId), onSuccess: refresh });
    const purgeMutation = useMutation({
        mutationFn: () => purgeClosedTenantNow(tenantId),
        onSuccess: async () => {
            setDialog(null);
            await refresh();
        },
    });

    if (stateQuery.isLoading) return null;
    const state = stateQuery.data;
    const name = businessName || confirmation;

    if (purgeMutation.isSuccess || (state && state.lifecycleState === 'unknown')) {
        return (
            <Alert severity="info" sx={{ mt: 3 }}>
                This business has been permanently deleted.
            </Alert>
        );
    }

    const closed = state?.lifecycleState === 'archived';
    return (
        <Paper variant="outlined" sx={{ mt: 3, p: 2, borderColor: 'error.light' }}>
            <Typography variant="h6" gutterBottom>Close and delete business</Typography>
            {closed ? (
                <Stack spacing={2}>
                    <Alert severity="warning">
                        {name} was closed on {formatDate(state?.closedAt ?? null)}. Its website is offline and its
                        address is free for another business. Its people see an &quot;account closed&quot; message
                        when they sign in. Everything, including their logins, will be permanently deleted on{' '}
                        <strong>{formatDate(state?.purgeAfter ?? null)}</strong>.{' '}
                        {state?.stripe ? stripeOutcome[state.stripe] ?? '' : ''}
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button variant="outlined" disabled={restoreMutation.isPending}
                            onClick={() => restoreMutation.mutate()}>
                            {restoreMutation.isPending ? 'Restoring…' : 'Restore business'}
                        </Button>
                        <Button color="error" onClick={() => setDialog('purge')}>Delete permanently now</Button>
                    </Box>
                    {restoreMutation.isError && <Alert severity="error">{restoreMutation.error.message}</Alert>}
                    {restoreMutation.isSuccess && (
                        <Alert severity="success">
                            Restored. Its Stripe subscription was not restarted; the owner needs to subscribe again.
                        </Alert>
                    )}
                </Stack>
            ) : (
                <Stack spacing={2}>
                    <Typography color="text.secondary">
                        Takes the website offline immediately, frees its address, and cancels its Stripe
                        subscription. The business can be restored for 30 days; after that its data, files and
                        logins are permanently deleted.
                    </Typography>
                    <Box>
                        <Button color="error" variant="outlined" onClick={() => setDialog('close')}>
                            Close and delete business
                        </Button>
                    </Box>
                </Stack>
            )}

            <ConfirmByTypingDialog
                open={dialog === 'close'}
                title={`Close ${name}?`}
                confirmation={confirmation}
                confirmLabel="Close business"
                pending={closeMutation.isPending}
                error={closeMutation.isError ? closeMutation.error.message : null}
                onClose={() => setDialog(null)}
                onConfirm={() => closeMutation.mutate()}
            >
                <Typography>This will immediately:</Typography>
                <Typography component="ul" sx={{ m: 0, pl: 3 }}>
                    <li>take the website offline and free its address</li>
                    <li>cancel the Stripe subscription (no refund is issued)</li>
                    <li>sign out everyone at this business and show them an &quot;account closed&quot; message</li>
                </Typography>
                <Typography>You can restore it for 30 days. After that it is deleted for good.</Typography>
            </ConfirmByTypingDialog>

            <ConfirmByTypingDialog
                open={dialog === 'purge'}
                title={`Permanently delete ${name}?`}
                confirmation={confirmation}
                confirmLabel="Delete permanently"
                pending={purgeMutation.isPending}
                error={purgeMutation.isError ? purgeMutation.error.message : null}
                onClose={() => setDialog(null)}
                onConfirm={() => purgeMutation.mutate()}
            >
                <Typography>
                    This deletes its bookings, customers, settings, files and its people&apos;s logins now instead
                    of on the scheduled date. It cannot be undone.
                </Typography>
            </ConfirmByTypingDialog>
        </Paper>
    );
};

export default TenantClosureSection;
